import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaClipboardList,
  FaSortAmountDown,
  FaSortAmountUp,
  FaTimesCircle,
  FaCheckCircle,
  FaClock,
  FaTruck,
  FaHardHat,
  FaArrowRight,
  FaTools,
} from "react-icons/fa";
import { getAllDocsSnap } from "../../utils/firebase/Firebase.utils";
import MobileWrapper from "../../components/MobileWrapper";

export default function AdminMobileConcerns() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);
  const [concerns, setConcerns] = useState([]);
  const [filteredConcerns, setFilteredConcerns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [isLoading, setIsLoading] = useState(true);

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  useEffect(() => {
    const fetchConcerns = async () => {
      await getAllDocsSnap("concerns", (docs) => {
        // Sort by creation date
        docs.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Newest first by default
        });
        setConcerns(docs);
        setFilteredConcerns(docs);
        setIsLoading(false);
      });
    };
    fetchConcerns();
  }, []);

  // Apply filters (search, status)
  useEffect(() => {
    let filtered = [...concerns];

    // First, filter by user role (if housekeeping, show only their concerns)
    if (currentUser.roles.includes("housekeeping")) {
      filtered = filtered.filter(
        (concern) => concern.user === currentUser.email
      );
    }

    // Then apply status filter
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (concern) => concern.status === selectedStatus
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((concern) => {
        // Search in concern type
        if (concern.concern?.toLowerCase().includes(term)) return true;

        // Search in description
        if (concern.description?.toLowerCase().includes(term)) return true;

        // Search in created by
        if (concern.createdBy?.toLowerCase().includes(term)) return true;

        // Search in unit identifier
        if (concern.identifier?.toLowerCase().includes(term)) return true;

        return false;
      });
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredConcerns(filtered);
  }, [searchQuery, concerns, selectedStatus, sortOrder, currentUser]);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "newest" ? "oldest" : "newest");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddNew = () => {
    navigate("/admin/mobile/add-concern");
  };

  const handleCardClick = (id) => {
    navigate(`/admin/mobile/concern/${id}`);
  };

  const getStatusCount = (status) => {
    // Get base concerns filtered by user role
    let baseFiltered = [...concerns];
    if (currentUser.roles.includes("housekeeping")) {
      baseFiltered = baseFiltered.filter(
        (concern) => concern.user === currentUser.email
      );
    }

    // For the "all" status count, always show the total count regardless of current filter
    if (status === "all") {
      // If there's a search query, apply it to baseFiltered
      if (searchQuery.trim()) {
        const term = searchQuery.toLowerCase().trim();
        return baseFiltered.filter((concern) => {
          if (concern.concern?.toLowerCase().includes(term)) return true;
          if (concern.description?.toLowerCase().includes(term)) return true;
          if (concern.createdBy?.toLowerCase().includes(term)) return true;
          if (concern.identifier?.toLowerCase().includes(term)) return true;
          return false;
        }).length;
      }
      // Otherwise just return the base filtered count
      return baseFiltered.length;
    }

    // For specific status counts
    // If there's a search query, filter baseFiltered by both search and status
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      return baseFiltered.filter((concern) => {
        // First check if status matches
        if (concern.status !== status) return false;

        // Then check search terms
        if (concern.concern?.toLowerCase().includes(term)) return true;
        if (concern.description?.toLowerCase().includes(term)) return true;
        if (concern.createdBy?.toLowerCase().includes(term)) return true;
        if (concern.identifier?.toLowerCase().includes(term)) return true;
        return false;
      }).length;
    }

    // If no search query, just filter by status
    return baseFiltered.filter((concern) => concern.status === status).length;
  };

  // Status styles and icons
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#FFA500"; // Orange
      case "Dispatched":
        return "#4299e1"; // Blue
      case "Processing":
        return "#9f7aea"; // Purple
      case "Resolved":
        return eleghoGreen;
      default:
        return "#808080"; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <FaClock />;
      case "Dispatched":
        return <FaTruck />;
      case "Processing":
        return <FaTools />;
      case "Resolved":
        return <FaCheckCircle />;
      default:
        return <FaClock />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <MobileWrapper label="Concerns">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            <p className="mt-2 text-sm text-[#363062]">Loading concerns...</p>
          </div>
        </div>
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper label="Concerns">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaTools className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Concern Requests
              </h1>
              <p className="text-xs text-gray-500">
                Manage maintenance and service requests
              </p>
            </div>
          </div>
          <button
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
            onClick={handleAddNew}
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by unit, concern type, description or creator..."
            value={searchQuery}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchQuery("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={toggleSortOrder}
            className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border border-gray-200 bg-white text-[#363062] hover:bg-gray-50"
            title={sortOrder === "newest" ? "Newest first" : "Oldest first"}
          >
            {sortOrder === "newest" ? (
              <div className="flex items-center gap-1">
                <FaSortAmountDown className="w-3 h-3" />
                <span>Newest</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <FaSortAmountUp className="w-3 h-3" />
                <span>Oldest</span>
              </div>
            )}
          </button>
        </div>

        {/* Status Filter Buttons */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                selectedStatus === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedStatus("all")}
            >
              All ({getStatusCount("all")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                selectedStatus === "Pending"
                  ? "bg-[#FFA500] text-white"
                  : "bg-[#FFA500]/10 text-gray-700 hover:bg-[#FFA500]/20"
              }`}
              onClick={() => setSelectedStatus("Pending")}
            >
              Pending ({getStatusCount("Pending")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                selectedStatus === "Dispatched"
                  ? "bg-[#4299e1] text-white"
                  : "bg-[#4299e1]/10 text-gray-700 hover:bg-[#4299e1]/20"
              }`}
              onClick={() => setSelectedStatus("Dispatched")}
            >
              Dispatched ({getStatusCount("Dispatched")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                selectedStatus === "Processing"
                  ? "bg-[#9f7aea] text-white"
                  : "bg-[#9f7aea]/10 text-gray-700 hover:bg-[#9f7aea]/20"
              }`}
              onClick={() => setSelectedStatus("Processing")}
            >
              Processing ({getStatusCount("Processing")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                selectedStatus === "Resolved"
                  ? "bg-[#8FCD00] text-white"
                  : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
              }`}
              onClick={() => setSelectedStatus("Resolved")}
            >
              Resolved ({getStatusCount("Resolved")})
            </button>
          </div>
        </div>

        {/* Concerns List */}
        <div className="mt-4 space-y-3 pb-20">
          {filteredConcerns.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaTools className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {filteredConcerns.length === 0
                  ? "No Concerns"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {filteredConcerns.length === 0
                  ? "No concerns have been submitted yet"
                  : "Try adjusting your filters or search terms"}
              </p>

              {currentUser.roles.includes("housekeeping") && (
                <button
                  onClick={handleAddNew}
                  className="mt-4 w-48 mx-auto bg-[#8FCD00] text-white py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-medium"
                >
                  <FaPlus className="w-3 h-3" />
                  <span>Add New Concern</span>
                </button>
              )}
            </div>
          ) : (
            filteredConcerns.map((concern) => (
              <div
                key={concern.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md hover:translate-y-[-2px] cursor-pointer"
                onClick={() => handleCardClick(concern.id)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {concern.identifier}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor: `${getStatusColor(concern.status)}15`,
                        color: getStatusColor(concern.status),
                      }}
                    >
                      <span className="text-xs">
                        {getStatusIcon(concern.status)}
                      </span>
                      {concern.status || "Unknown"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaTools className="text-gray-400 flex-shrink-0" />
                      <span className="font-medium">{concern.concern}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaHardHat className="text-gray-400 flex-shrink-0" />
                      <span>{concern.createdBy}</span>
                    </div>
                  </div>

                  {/* Description Section */}
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1.5">
                      Description:
                    </div>
                    <div className="text-xs line-clamp-2">
                      {concern.description}
                    </div>
                  </div>

                  {/* Media Preview */}
                  {(concern.image || concern.video) && (
                    <div className="mt-3">
                      {concern.image && (
                        <div className="w-full h-24 rounded-lg overflow-hidden">
                          <img
                            src={concern.image}
                            alt="Concern"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {concern.video && !concern.image && (
                        <div className="w-full h-24 rounded-lg overflow-hidden">
                          <video
                            src={concern.video}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Footer with View Details */}
                <div className="flex justify-between items-center py-2 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    {formatDate(concern.createdAt)}
                  </div>
                  <div className="flex items-center text-xs font-medium text-[#363062]">
                    View Details
                    <FaArrowRight className="ml-1.5 w-3 h-3" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MobileWrapper>
  );
}
